import { FC, useCallback, useContext, useState } from "react";
import { SportFavoritesContext, PrematchFeedContext } from "@finbackoffice/site-core";
import { ModalsContextV2 } from "contexts";
import Loading from "components/base/loading/Loading";
import { ISportFeedResponse } from "@finbackoffice/websocket-client";
import { PrematchContext } from "contexts";
import AdvancedSidebarPrematchSport from "components/side-bar/advanced/prematch/sport/AdvancedSidebarPrematchSport";
import Button from "components/base/button/Button";
import classNames from "classnames";
import { usePathname, useRouter } from "next/navigation";
import ActiveLink from "components/base/active-link/ActiveLink";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import Popover from "components/base/popover/Popover";
import Sport from "components/sport/base/sport/Sport";
import { useDefaultPrematchParamsFromUrl } from "hooks";
import { routeFavorites, routeTopLeagues, routeUpcoming } from "components/side-bar/Sidebar";
import { MainSidebarContext } from "../MainSidebar";
import styles from "./main-sidebar-prematch.module.sass";

const MainSidebarPrematch: FC = () => {
    const router = useRouter();
    const pathname = usePathname();

    const { selectedSportId, setSelectedSportId, scrollRef } = useContext(MainSidebarContext);
    const { prematchFeed, prematchIsLoading } = useContext(PrematchFeedContext);
    const { isSportSelected } = useContext(PrematchContext);
    const { favPrematchLeaguesGameCount } = useContext(SportFavoritesContext);
    const { advancedSidebarModalRef } = useContext(ModalsContextV2);

    const { sportSlug } = useDefaultPrematchParamsFromUrl();

    const [popoverLastPosition, setPopoverLastPosition] = useState<{
        top: number | null;
        left: number | null;
    }>({ top: null, left: null });

    const handleClose = useCallback(() => {
        setSelectedSportId(null);
    }, [setSelectedSportId]);

    const handleSetSelectedSport = useCallback(
        (id: number) => {
            if (id === selectedSportId) {
                handleClose();
            } else {
                setSelectedSportId(id);
            }
        },
        [handleClose, selectedSportId, setSelectedSportId],
    );

    const handleFavoriteClick = () => {
        router.push(routeFavorites);
    };

    return (
        <>
            <div className={styles.subNav}>
                <Button
                    type="custom"
                    disabled={favPrematchLeaguesGameCount === 0}
                    className={classNames(styles.subNavItem, {
                        [styles.selected]: pathname?.includes(routeFavorites),
                    })}
                    onClick={handleFavoriteClick}>
                    <>
                        <span className={styles.gamesCount}>{favPrematchLeaguesGameCount}</span>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/favorite-events.svg`}
                            className="svg-icon"
                        />
                        <Translate tid="sport_favorite_events" />
                    </>
                </Button>
                <ActiveLink
                    href={routeTopLeagues}
                    includes={routeTopLeagues}
                    activeClassName={styles.selected}
                    className={styles.subNavItem}>
                    <>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/top-leagues.svg`}
                            className="svg-icon"
                        />
                        <Translate
                            tid="prematch_topLeagues"
                            namespace={TranslationScopes.PreMatch}
                        />
                    </>
                </ActiveLink>
                <ActiveLink
                    href={routeUpcoming}
                    includes={routeUpcoming}
                    activeClassName={styles.selected}
                    className={styles.subNavItem}>
                    <>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/upcoming-matches.svg`}
                            className="svg-icon"
                        />
                        <Translate
                            tid="prematch_upcoming_matches"
                            namespace={TranslationScopes.PreMatch}
                        />
                    </>
                </ActiveLink>
            </div>
            <Button
                type="button"
                variant="custom"
                onClick={() => {
                    advancedSidebarModalRef.current?.open();
                }}
                className={styles.allBtn}>
                <Translate tid="prematch_allMatches" namespace={TranslationScopes.PreMatch} />
            </Button>
            <div className={styles.subNav}>
                {!prematchIsLoading ? (
                    <>
                        {prematchFeed.map((sport: ISportFeedResponse, index: number) => (
                            <Popover
                                key={index}
                                scrollRef={scrollRef}
                                scrollBehavior="close"
                                contentClassName={styles.popoverContent}
                                placement="right"
                                initialPosition={popoverLastPosition}
                                onPositionChange={(position) => {
                                    setPopoverLastPosition(position);
                                }}
                                onOpen={() => handleSetSelectedSport(sport.id)}
                                onOutsideClick={(event) => {
                                    const target = event?.target as Element;
                                    if (!target?.closest(".popover-trigger-container")) {
                                        setPopoverLastPosition({ top: null, left: null });
                                    }
                                }}
                                trigger={
                                    <Sport
                                        key={sport.id}
                                        sport={sport}
                                        hasFavorite={false}
                                        variant="sidebar"
                                        type="button"
                                        isSelected={
                                            isSportSelected(sport.id) || sportSlug === sport.slug
                                        }
                                    />
                                }>
                                <AdvancedSidebarPrematchSport
                                    sport={sport}
                                    openedByDefault
                                    disableCollapsing
                                />
                            </Popover>
                        ))}
                    </>
                ) : (
                    <Loading />
                )}
            </div>
        </>
    );
};

export default MainSidebarPrematch;
